import { useQuery } from '@tanstack/react-query';
import { agencyApi } from '../api/agencyApi';
import { agencyKeys } from '../queryKeys';

/**
 * The paginated practice list and the agency stat cards went with
 * /agency/practices — the Portfolio view loads every practice with its health
 * and metrics in one call, so there is nothing left to paginate or count
 * separately.
 */
export function usePracticeDetail(id: string) {
  return useQuery({
    queryKey: agencyKeys.practice(id),
    queryFn: () => agencyApi.getPractice(id),
    select: (res) => res.data,
    enabled: !!id,
  });
}

// Alias kept for compatibility
export const usePractice = usePracticeDetail;

/**
 * Every active practice (not inactive, not snoozed), sorted by name — the list
 * the practice switchers offer. Same query and cache as the top-bar switcher.
 */
export function useActivePractices(enabled = true) {
  return useQuery({
    queryKey: agencyKeys.allPractices(),
    // The backend caps limit at 2000.
    queryFn: async () => (await agencyApi.getPractices({ limit: 2000 })).data,
    select: (practices) =>
      practices
        .filter((p) => p.status !== 'inactive' && !p.snoozedAt)
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    enabled,
  });
}
